import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import i18n from 'i18next';
import { action, makeObservable, observable } from 'mobx';

import { getUserLanguage } from '../../i18n/helpers';
import { resolveFlag } from '../../services/launchDarkly';
import { devLog } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import type { UserFreeSpinsData } from './types';
import { fetchUserFreeSpins } from './utils';

// Mirrors mini-games-ui's BaroulettaErrorEnum (src/context/types.ts) — the codes a game posts in
// its `gameError` payload. Only codes meaning "launching again can never succeed" belong here;
// anything else is treated as transient and keeps the widget as the player's retry/re-entry point.
const PERMANENT_GAME_ERROR_CODES = new Set<number>([
  1, // E_INSUFFICIENT_BALANCE — no rounds left to play
  3, // E_BET_NOT_ALLOWED
  6, // E_PLAYER_IS_FROZEN
  8, // E_GAME_NOT_FOUND
  13, // E_FREE_ROUND_NOT_EXISTS
  120, // E_INTERNAL_SERVER_ERROR_NO_RETRY
]);

export class UserFreeSpinsStore extends Loadable implements IFeatureManagerStore {
  private readonly rootStore: RootStore;
  private prevLang = getUserLanguage();

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;

        const featureFlag = resolveFlag('user_free_spins_widgets_feature', true);

        if (!featureFlag) {
          return;
        }

        fetchUserFreeSpins()
          .then((freeSpinsResponse) => {
            this.setUserFreeSpinsData(freeSpinsResponse.data.data.freeSpinsData);
          })
          .catch((error) => {
            devLog('Error fetching user free spins', error);
          });
      }
    });
  }

  private dialogsQueue: string[] = [];
  private timerInterval: NodeJS.Timeout | undefined = undefined;

  dialogTypes: FeatureManagerDialogType[] = ['UserFreeSpins'];

  @observable
  public selectedFreeSpin: UserFreeSpinsData | null = null;

  @observable
  public userFreeSpinsData: UserFreeSpinsData[] = [];

  @observable
  public isDialogOpen = false;

  @observable
  public timers: Record<string, number> = {};

  // Filters the dialog queue on refetch; server row stays pending until consumed.
  private claimedIds = new Set<string>();

  // Filters userFreeSpinsData on refetch so failed claims don't reappear this session.
  private erroredIds = new Set<string>();

  private currentlyClaimingId: string | null = null;

  @action
  public openDialog() {
    this.isDialogOpen = true;
  }

  @action
  public closeDialog() {
    this.selectedFreeSpin = null;
    this.isDialogOpen = false;
  }

  @action
  private setTimers(timers: Record<string, number>) {
    this.timers = timers;
  }

  private calculateExpirationTime(freeSpin: UserFreeSpinsData) {
    if (!freeSpin.createdAt) {
      return 0;
    }

    const createdAt = dayjs(freeSpin.createdAt);
    const createdAtPlus24h = createdAt.add(24, 'hour');
    const bonusCodeExpirationTime = freeSpin.endDateTime || freeSpin.endDate || freeSpin.expirationDate;

    // Use whichever is earlier: createdAt + 24h OR bonusEndTime
    if (!bonusCodeExpirationTime) {
      const secondsToEnd = createdAtPlus24h.diff(dayjs(), 'second');
      return Math.max(0, secondsToEnd);
    }

    const expirationTime = dayjs(bonusCodeExpirationTime);
    const endTime = expirationTime.isBefore(createdAtPlus24h) ? expirationTime : createdAtPlus24h;

    const secondsToEnd = endTime.diff(dayjs(), 'second');
    return Math.max(0, secondsToEnd);
  }

  private startTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      const timers = { ...this.timers };
      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          delete timers[key];
          this.removeFreeSpin(key);
        }
      });
      this.setTimers(timers);
    }, 1000);
  }

  @action
  public removeFreeSpin(id: string) {
    this.userFreeSpinsData = this.userFreeSpinsData.filter((spin) => spin.id !== id);
    this.dialogsQueue = this.dialogsQueue.filter((spinId) => spinId !== id);

    if (this.selectedFreeSpin?.id === id) {
      this.selectedFreeSpin = null;
      if (this.isDialogOpen) {
        this.closeDialog();
      }
    }
  }

  // Soft claim: keep the row in `userFreeSpinsData` (widget needs it as a re-entry point
  // if the player backs out) but stop the auto-popup from re-triggering this session.
  @action
  public markClaimed(id: string) {
    this.claimedIds.add(id);
    this.currentlyClaimingId = id;
    this.dialogsQueue = this.dialogsQueue.filter((spinId) => spinId !== id);
  }

  @action
  public markErrored(errorCode?: number | null) {
    const id = this.currentlyClaimingId;
    if (!id) return;
    this.currentlyClaimingId = null;
    // Transient failure (retryable server codes, timeouts, or the null that Crown Rise /
    // Crown Striker post): keep the row — the widget is the player's only way back into an
    // interrupted session, and removing it strands a resumable game behind a one-off error.
    if (errorCode == null || !PERMANENT_GAME_ERROR_CODES.has(errorCode)) return;
    this.erroredIds.add(id);
    this.removeFreeSpin(id);
  }

  public getFreeSpinTimerFormatted(id: string, t?: TFunction): string {
    const timerSecs = this.timers[id];
    if (timerSecs && timerSecs > 0) {
      return formatTime(timerSecs, 's', t);
    }
    return '';
  }

  // A fully-played reward must not resurface as a claimable widget/dialog — launching its
  // game again just errors (e.g. a wheel with no spins left). The server row can stay
  // pending until it expires, so hide it client-side once all rounds are used.
  private isSpentReward(spin: UserFreeSpinsData): boolean {
    const rounds = Number(spin.rounds);
    const progress = Number(spin.progress);
    return rounds > 0 && Number.isFinite(progress) && progress >= rounds;
  }

  @action
  private setUserFreeSpinsData(data: UserFreeSpinsData[]) {
    this.userFreeSpinsData = data.filter((spin) => !this.erroredIds.has(spin.id) && !this.isSpentReward(spin));

    // initilalize timers
    const timers: Record<string, number> = {};
    this.userFreeSpinsData.forEach((spin) => {
      const secondsToEnd = this.calculateExpirationTime(spin);
      if (secondsToEnd > 0) {
        timers[spin.id] = secondsToEnd;
      }

      this.setTimers(timers);

      // Start timer interval if there are active timers
      if (Object.keys(timers).length > 0) {
        this.startTimers();
      } else if (this.timerInterval) {
        // Clear interval if no timers
        clearInterval(this.timerInterval);
        this.timerInterval = undefined;
      }
    });
  }

  @action
  public openFreeSpinDialogById(id: string) {
    const freeSpin = this.userFreeSpinsData.find((spin) => spin.id === id);
    if (freeSpin) {
      this.selectedFreeSpin = freeSpin;
      this.openDialog();
    }
  }

  public startDialogs(type?: FeatureManagerDialogType) {
    if (type === 'UserFreeSpins' && this.rootStore.authStore.authorized) {
      // Skip when a minigame is already opening — the FS that triggered this dialog is
      // being launched directly (e.g., daily-bonus claim auto-launches the wheel),
      // and the FreeSpinDialog "Claim" intermediary would render behind that popup.
      const { minigamesStore } = this.rootStore;
      if (minigamesStore.isDialogOpen || minigamesStore.loadingMinigame) {
        return false;
      }
      return this.showNextDialog();
    }

    return false;
  }

  public showNextDialog() {
    if (this.dialogsQueue.length > 0) {
      this.openFreeSpinDialogById(this.dialogsQueue.shift()!);
      return true;
    }
    return false;
  }

  public cleanup() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    this.timers = {};
  }

  @action
  public reset() {
    this.cleanup();
    this.userFreeSpinsData = [];
    this.selectedFreeSpin = null;
    this.isDialogOpen = false;
    this.dialogsQueue = [];
    this.claimedIds.clear();
    this.erroredIds.clear();
    this.currentlyClaimingId = null;
    this.setLoading(false);
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    const featureFlag = resolveFlag('user_free_spins_widgets_feature', true);
    if (!featureFlag) return 'OFF';

    this.setLoading(true);
    try {
      const freeSpinsResponse = await fetchUserFreeSpins();
      this.setUserFreeSpinsData(freeSpinsResponse.data.data.freeSpinsData);
      this.dialogsQueue = this.userFreeSpinsData.map((s) => s.id).filter((id) => !this.claimedIds.has(id));
      return 'READY';
    } catch (error) {
      devLog('Error fetching user free spins', error);
      return 'ERROR';
    } finally {
      this.setLoading(false);
    }
  });
}
